import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getConsentInfo, submitConsent } from '../api/integrations';
import { ConsentResponse } from '../types/integrations';
import { ShieldCheck, ShieldAlert, CheckCircle, XCircle, Globe, Lock } from 'lucide-react';

export const ConsentPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [consentData, setConsentData] = useState<ConsentResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set(['vocab:read']));

  // Extract ticket from URL without logging or displaying it
  const ticket = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('ticket') || ''
    : '';

  useEffect(() => {
    if (!ticket) {
      setErrorMessage('未检测到授权票据 (ticket)，无法处理授权请求。');
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const data = await getConsentInfo(ticket);
        if (isMounted) {
          setConsentData(data);
          // Only vocab:read is checked by default; write and export must be opted in by the user
          const initial = new Set<string>();
          if (data.scopes.some((s) => s.id === 'vocab:read')) {
            initial.add('vocab:read');
          }
          setSelectedScopes(initial);
        }
      } catch (err: any) {
        if (isMounted) {
          setErrorMessage(err.message || '授权请求已过期或不存在，请重新在客户端发起。');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [ticket]);

  const toggleScope = (scopeId: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scopeId)) {
        next.delete(scopeId);
      } else {
        next.add(scopeId);
      }
      return next;
    });
  };

  const handleDecision = async (approve: boolean) => {
    if (approve && selectedScopes.size === 0) {
      showToast('请至少勾选一项要授予的权限', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitConsent({
        ticket,
        approve,
        scopes: approve ? Array.from(selectedScopes) : [],
      });
      if (res.redirect_url) {
        window.location.assign(res.redirect_url);
      } else {
        showToast('授权响应未包含有效重定向地址', 'error');
        setSubmitting(false);
      }
    } catch (err: any) {
      showToast(err.message || '提交授权决定失败，请重试', 'error');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium">正在拉取 OAuth 客户端授权详情...</p>
      </div>
    );
  }

  if (errorMessage || !consentData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/60 to-red-50/20 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-6 shadow-xl shadow-slate-200/50 sm:rounded-2xl sm:px-10 border border-slate-200/80 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">授权请求无效</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">
              {errorMessage || '无法识别当前授权票据，可能已被使用或已超时。'}
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/';
              }}
              className="w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-xl transition-colors shadow-sm"
            >
              返回 Lexi 首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { client, scopes } = consentData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/60 to-emerald-50/30 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white shadow-xl shadow-slate-200/50 sm:rounded-2xl border border-slate-200/80 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 text-white text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 backdrop-blur border border-white/20 text-white mb-3 shadow-inner">
              <Lock className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold tracking-tight">应用授权申请</h1>
            <p className="text-xs text-emerald-100 mt-1">
              当前登录账户：<span className="font-semibold text-white">{user?.name || user?.id}</span>
            </p>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            {/* Client Info Card */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                    第三方应用 / MCP 客户端
                  </p>
                  <p className="text-base font-bold text-slate-900 mt-0.5 truncate">
                    {client.name || '外部应用'}
                  </p>
                  {client.uri && (
                    <p className="text-xs text-slate-500 mt-0.5 font-mono break-all select-all">
                      {client.uri}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-200/70 leading-relaxed">
                该应用正请求访问您的 Lexi 英语单词与间隔记忆库。请勾选确认您允许授予的权限。
              </p>
            </div>

            {/* Scope Permissions List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  权限授权范围
                </span>
                <span className="text-[11px] text-slate-500">
                  仅已勾选的权限会被授予
                </span>
              </div>

              <div className="space-y-2.5">
                {scopes.map((scope) => {
                  const isChecked = selectedScopes.has(scope.id);
                  const isReadOnly = scope.id === 'vocab:read';
                  const isExport = scope.id === 'data:export';

                  return (
                    <label
                      key={scope.id}
                      className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                        isChecked
                          ? 'bg-emerald-50/50 border-emerald-300 ring-1 ring-emerald-300/40'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleScope(scope.id)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                            {scope.id}
                          </span>
                          {isReadOnly && (
                            <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100/70 px-1.5 py-0.5 rounded">
                              默认勾选 · 只读
                            </span>
                          )}
                          {!isReadOnly && !isExport && (
                            <span className="text-[10px] font-medium text-amber-700 bg-amber-100/70 px-1.5 py-0.5 rounded">
                              写入权限 · 需主动勾选
                            </span>
                          )}
                          {isExport && (
                            <span className="text-[10px] font-medium text-purple-700 bg-purple-100/70 px-1.5 py-0.5 rounded">
                              全量导出 · 需主动勾选
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                          {scope.label || scope.id}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5 text-xs text-amber-800 leading-relaxed">
              <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                授权后，客户端将通过 OAuth 2.0 PKCE 令牌安全访问相应接口。您可随时在「MCP/API」导航中单方面一键撤销连接。
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleDecision(false)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-4 text-sm font-medium text-slate-700 hover:text-red-700 bg-slate-100 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-xl transition-colors disabled:opacity-50 cursor-pointer"
              >
                <XCircle className="w-4 h-4 text-slate-400 group-hover:text-red-600" />
                {submitting ? '处理中...' : '拒绝'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleDecision(true)}
                className="flex-2 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm hover:shadow transition-all disabled:opacity-50 cursor-pointer"
              >
                <CheckCircle className="w-4 h-4" />
                {submitting ? '正在提交授权...' : '同意并授权'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <span className="text-xs text-slate-400">
            Lexi 单词平台 • OAuth 2.0 授权服务器
          </span>
        </div>
      </div>
    </div>
  );
};
